import { UnicoResource } from "@repo/core/services/resources/unico";
import { UnicoCreateProcessResponse } from "@repo/core/services/entities/Unico";

/**
 * Fetches selfie from GET /selfie when getProcess did not include fileContents
 * (e.g. transient Unico/API failure). Needed for idtrust / biometry-only flows.
 *
 * When selfie is still missing and biometry is required, loads the probatory set
 * (PDF) from GET /evidenceset and attaches it as `selfie` so BIOMETRY (0026)
 * upload and RegistrarConsulta SELFIE behave like a normal selfie.
 *
 * If evidenceset is unavailable (e.g. 400 with Unico code 9, "evidence is not available"),
 * leaves selfie unset so the flow continues with manual capture where applicable.
 */
export const enrichProcessDataWithSelfieWhenMissing = async (
  unicoResource: UnicoResource,
  processId: string,
  processData: UnicoCreateProcessResponse,
  requiresBiometry: boolean
): Promise<UnicoCreateProcessResponse> => {
  if (!requiresBiometry || !processId || processData.selfie?.fileContents) {
    return processData;
  }
  try {
    const { data } = await unicoResource.getSelfie(processId);
    if (data?.fileContents) {
      return { ...processData, selfie: data };
    }
  } catch {
    // Optional when getProcess already merged selfie from backend
  }

  try {
    const { data } = await unicoResource.getEvidenceSet(processId);
    const b64 = data?.fileContents?.trim();
    if (b64) {
      return {
        ...processData,
        selfie: {
          fileContents: data.fileContents,
          // Unico may report x-pdf; proposal upload expects a standard MIME type
          contentType: "application/pdf"
        }
      };
    }
  } catch {
    // e.g. 400 code 9 — evidence not available; do not attach, stay on manual path
  }
  return processData;
};
